import type { CurrentUserDetails } from '@unifyapps/network/generated/models/currentUserDetails';
import { useEffect, useMemo, useRef } from 'react';

const getAdaptedCurrentUserDetails = (
  currentUserDetails: CurrentUserDetails | undefined,
  initialLocale?: string,
) => {
  return {
    ...currentUserDetails,
    user: {
      ...currentUserDetails?.user,
      locale: initialLocale,
    },
  };
};

export function useAdaptedUserDetails({
  userDetails,
  initialLocale,
  isLoading,
}: {
  userDetails: CurrentUserDetails | undefined;
  initialLocale?: string;
  isLoading: boolean;
}) {
  const isInitialLocaleAppliedRef = useRef(false);

  const adaptedUserDetails = useMemo(() => {
    //initial locale is only supplied from interface so it will affect only interface and no other modules
    if (isInitialLocaleAppliedRef.current || isLoading || !initialLocale) {
      return userDetails;
    }

    const adapted = getAdaptedCurrentUserDetails(userDetails, initialLocale);

    return adapted;
  }, [userDetails, initialLocale, isLoading]);

  //this effect is uded to initialise the initialLocale once in the user.
  //once initialLocale is applied, we set the ref to true to avoid applying it again.
  useEffect(() => {
    if (!isLoading && initialLocale && !isInitialLocaleAppliedRef.current) {
      isInitialLocaleAppliedRef.current = true;
    }
  }, [isLoading, initialLocale]);

  return adaptedUserDetails;
}
